import { Text } from '@fluentui/react-components';

import type { UsageWeeklyTrendProps } from './DatasetUsageSection.interface.js';
import { useStyles } from './DatasetUsageSection.style.js';

// SVG coordinate system. The chart scales responsively via `viewBox`; these are
// abstract units, not pixels.
const VIEWBOX_WIDTH = 640;
const VIEWBOX_HEIGHT = 200;
const PADDING_LEFT = 44;
const PADDING_RIGHT = 16;
const PADDING_TOP = 20;
const PADDING_BOTTOM = 32;
const BAR_WIDTH_RATIO = 0.55;
const BAR_RADIUS = 2;
const MIN_MAX_COUNT = 1;
const AXIS_FONT_SIZE = 11;
const VALUE_FONT_SIZE = 12;
const Y_LABEL_GAP = 8;
const VALUE_LABEL_GAP = 6;
const X_LABEL_GAP = 16;

const PLOT_WIDTH = VIEWBOX_WIDTH - PADDING_LEFT - PADDING_RIGHT;
const PLOT_HEIGHT = VIEWBOX_HEIGHT - PADDING_TOP - PADDING_BOTTOM;
const BASELINE_Y = PADDING_TOP + PLOT_HEIGHT;
const AXIS_LABEL_X = PADDING_LEFT - Y_LABEL_GAP;

const TREND_TITLE = 'Weekly Trend';
const CHART_ARIA_LABEL = 'Weekly usage trend (interactions per week)';

export function UsageWeeklyTrend({ weeks }: UsageWeeklyTrendProps) {
  const styles = useStyles();

  if (weeks.length === 0) {
    return null;
  }

  const maxCount = Math.max(...weeks.map((week) => week.count), MIN_MAX_COUNT);
  const slot = PLOT_WIDTH / weeks.length;
  const barWidth = slot * BAR_WIDTH_RATIO;

  return (
    <div className={styles.trendCard}>
      <Text className={styles.breakdownTitle}>{TREND_TITLE}</Text>
      <svg
        className={styles.trendSvg}
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
        role="img"
        aria-label={CHART_ARIA_LABEL}
        preserveAspectRatio="xMidYMid meet"
      >
        <line
          className={styles.gridLine}
          x1={PADDING_LEFT}
          y1={PADDING_TOP}
          x2={PADDING_LEFT + PLOT_WIDTH}
          y2={PADDING_TOP}
        />
        <line
          className={styles.axisLine}
          x1={PADDING_LEFT}
          y1={BASELINE_Y}
          x2={PADDING_LEFT + PLOT_WIDTH}
          y2={BASELINE_Y}
        />
        <text
          className={styles.axisText}
          x={AXIS_LABEL_X}
          y={PADDING_TOP}
          textAnchor="end"
          dominantBaseline="middle"
          fontSize={AXIS_FONT_SIZE}
        >
          {maxCount}
        </text>
        <text
          className={styles.axisText}
          x={AXIS_LABEL_X}
          y={BASELINE_Y}
          textAnchor="end"
          dominantBaseline="middle"
          fontSize={AXIS_FONT_SIZE}
        >
          0
        </text>
        {weeks.map((week, index) => {
          const barHeight = (week.count / maxCount) * PLOT_HEIGHT;
          const barX = PADDING_LEFT + index * slot + (slot - barWidth) / 2;
          const barY = BASELINE_Y - barHeight;
          const centerX = PADDING_LEFT + index * slot + slot / 2;
          return (
            <g key={week.weekStart}>
              {week.count > 0 && (
                <rect
                  className={styles.trendBar}
                  x={barX}
                  y={barY}
                  width={barWidth}
                  height={barHeight}
                  rx={BAR_RADIUS}
                >
                  <title>{`${week.label}: ${week.count}`}</title>
                </rect>
              )}
              {week.count > 0 && (
                <text
                  className={styles.valueText}
                  x={centerX}
                  y={barY - VALUE_LABEL_GAP}
                  textAnchor="middle"
                  fontSize={VALUE_FONT_SIZE}
                >
                  {week.count}
                </text>
              )}
              <text
                className={styles.axisText}
                x={centerX}
                y={BASELINE_Y + X_LABEL_GAP}
                textAnchor="middle"
                fontSize={AXIS_FONT_SIZE}
              >
                {week.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
